/**
 * Precision Preset Quantity Module
 * =================================
 * 
 * Handles quantity-based price updates and preset quantity selection (1, 2, 3 bottles).
 * 
 * @module components/precision-preset-quantity
 * 
 * ## Business Logic
 * 
 * | Purchase Type | Discount Applied |
 * |---------------|------------------|
 * | One-time      | Quantity discount (e.g., 10% for 2, 15% for 3) |
 * | Subscription  | Subscription discount only (no quantity discount) |
 * 
 * ## Functions
 * 
 * - `initQuantityPriceUpdates()` - Core logic for quantity-based price updates (always runs)
 * - `initPresetQuantity()` - Preset UI logic (only runs if presets are enabled)
 * 
 * ## Events (set up at module load time)
 * 
 * - Listens: `selling-plan:changed` - Updates subscription state and price display
 * - Dispatches: `quantity:changed` - Fired when quantity changes
 */

import { updatePriceDisplay, getPriceDisplayContainer } from './precision-price-display.js';

// Shared state
let currentQuantity = 1;
let isSubscription = false;
let subscriptionUnitPrice = 0;
let oneTimeUnitPrice = 0;
let preset2Discount = 0;
let preset3Discount = 0;
let moneyFormat = '${{amount}}';
let isInitialized = false;
let hasPurchaseOptions = false; // Whether subscription options are available
let compareAtPrice = 0; // Variant compare_at_price from Shopify

/**
 * Get quantity discount percentage
 * Only applies to one-time purchases
 */
function getQuantityDiscount(quantity) {
  if (isSubscription) return 0;
  
  if (quantity === 2) return preset2Discount;
  if (quantity === 3) return preset3Discount;
  return 0;
}

/**
 * Get effective per-unit price based on current state
 */
function getEffectiveUnitPrice() {
  if (isSubscription) {
    return subscriptionUnitPrice;
  }
  
  const discount = getQuantityDiscount(currentQuantity);
  const discountAmount = Math.round(oneTimeUnitPrice * (discount / 100));
  return oneTimeUnitPrice - discountAmount;
}

/**
 * Update the main price display
 * - Shows total price when purchase options are NOT available
 * - Shows unit price when purchase options ARE available
 */
function updateUnitPriceDisplay() {
  const priceDisplay = getPriceDisplayContainer();
  if (!priceDisplay) return;

  const effectiveUnitPrice = getEffectiveUnitPrice();
  const originalUnitPrice = oneTimeUnitPrice;
  
  // If no purchase options, show total price (quantity × unit price)
  // If purchase options available, show unit price
  if (!hasPurchaseOptions) {
    const totalPrice = effectiveUnitPrice * currentQuantity;
    const originalTotalPrice = originalUnitPrice * currentQuantity;
    const compareAtTotal = compareAtPrice > 0 ? compareAtPrice * currentQuantity : originalTotalPrice;
    const effectiveCompare = Math.max(originalTotalPrice, compareAtTotal);
    const hasDiscount = totalPrice < effectiveCompare;

    updatePriceDisplay({
      container: priceDisplay,
      currentPrice: totalPrice,
      comparePrice: effectiveCompare,
      moneyFormat,
      showCompare: hasDiscount
    });
  } else {
    // For one-time: use compareAtPrice if higher than oneTimeUnitPrice
    const effectiveCompare = (!isSubscription && compareAtPrice > originalUnitPrice)
      ? compareAtPrice
      : originalUnitPrice;
    const hasDiscount = effectiveUnitPrice < effectiveCompare;

    updatePriceDisplay({
      container: priceDisplay,
      currentPrice: effectiveUnitPrice,
      comparePrice: effectiveCompare,
      moneyFormat,
      showCompare: hasDiscount
    });
  }
}

/**
 * Dispatch quantity changed event for purchase options to listen
 */
function dispatchQuantityChanged() {
  const event = new CustomEvent('quantity:changed', {
    detail: {
      quantity: currentQuantity,
      unitPrice: getEffectiveUnitPrice(),
      oneTimeUnitPrice,
      subscriptionUnitPrice,
      isSubscription,
      quantityDiscount: getQuantityDiscount(currentQuantity)
    },
    bubbles: true
  });
  document.dispatchEvent(event);
}

/**
 * Set subscription state
 */
function setSubscriptionState(isSubActive, subPrice) {
  isSubscription = isSubActive;
  subscriptionUnitPrice = subPrice || oneTimeUnitPrice;
}

/**
 * Hide/show discount badges based on subscription state
 */
function updateDiscountBadges(isSubActive) {
  const presetContainer = document.querySelector('[data-precision-preset-quantity]');
  if (!presetContainer) return;
  
  const discountBadges = presetContainer.querySelectorAll('[data-preset-discount-badge]');
  discountBadges.forEach(badge => {
    if (isSubActive) {
      badge.classList.add('tw-hidden');
    } else {
      badge.classList.remove('tw-hidden');
    }
  });
}

// ============================================================
// Event Listeners - Set up at module load time (not in init)
// ============================================================

/**
 * Listen for subscription selection changes
 * Updates pricing when user switches between one-time and subscription
 */
document.addEventListener('selling-plan:changed', function(e) {
  if (!isInitialized) return;
  if (!e.detail || e.detail.isSubscription === undefined) return;
  
  // Update discount badges visibility
  updateDiscountBadges(e.detail.isSubscription);
  
  // Update subscription state and recalculate prices
  setSubscriptionState(e.detail.isSubscription, e.detail.subscriptionPrice);
  updateUnitPriceDisplay();
  dispatchQuantityChanged();
});

// ============================================================
// Initialization Functions
// ============================================================

/**
 * Initialize core quantity-based price updates
 * This runs regardless of whether presets are enabled
 */
export function initQuantityPriceUpdates() {
  const quantityInput = document.querySelector('[data-quantity-input]');
  const priceDisplay = getPriceDisplayContainer();
  const presetContainer = document.querySelector('[data-precision-preset-quantity]');
  const purchaseOptionsContainer = document.querySelector('[data-precision-purchase-options]');
  
  // Get price data from available sources (priority: preset > purchase options > price display)
  if (presetContainer) {
    oneTimeUnitPrice = parseInt(presetContainer.dataset.unitPrice) || 0;
    preset2Discount = parseInt(presetContainer.dataset.discountQty2) || 0;
    preset3Discount = parseInt(presetContainer.dataset.discountQty3) || 0;
    moneyFormat = presetContainer.dataset.moneyFormat || '${{amount}}';
    hasPurchaseOptions = presetContainer.dataset.hasPurchaseOptions === 'true';
  } else if (purchaseOptionsContainer) {
    oneTimeUnitPrice = parseInt(purchaseOptionsContainer.dataset.oneTimePrice) || 0;
    moneyFormat = purchaseOptionsContainer.dataset.moneyFormat || '${{amount}}';
  } else if (priceDisplay) {
    // Fallback: get price directly from price display (no presets, no subscriptions)
    oneTimeUnitPrice = parseInt(priceDisplay.dataset.unitPrice) || 0;
    moneyFormat = priceDisplay.dataset.moneyFormat || '${{amount}}';
  }

  // Read variant compare_at_price from price display container
  if (priceDisplay) {
    compareAtPrice = parseInt(priceDisplay.dataset.compareAtPrice) || 0;
  }
  
  subscriptionUnitPrice = oneTimeUnitPrice;

  // Always show initial unit price (even without presets or purchase options)
  if (priceDisplay && oneTimeUnitPrice > 0) {
    updateUnitPriceDisplay();
  }

  // Core quantity change handler
  if (quantityInput) {
    quantityInput.addEventListener('change', function () {
      const newQuantity = parseInt(this.value) || 1;
      if (newQuantity === currentQuantity) return;
      
      currentQuantity = newQuantity;
      updateUnitPriceDisplay();
      dispatchQuantityChanged();
    });
  }

  // Mark as initialized so event listeners start working
  isInitialized = true;

  // Expose methods globally
  window.precisionQuantity = {
    getCurrentQuantity: () => currentQuantity,
    getEffectiveUnitPrice,
    updateUnitPriceDisplay,
    dispatchQuantityChanged,
    setSubscriptionState
  };
}

/**
 * Initialize preset quantity UI functionality
 * Only runs if preset quantity container exists
 */
export function initPresetQuantity() {
  const container = document.querySelector('[data-precision-preset-quantity]');
  if (!container) return;

  const options = container.querySelectorAll('[data-preset-option]');
  const quantityInput = document.querySelector('[data-quantity-input]');

  function updateSelectedState(selectedOption) {
    options.forEach(opt => {
      opt.classList.remove('is-selected');
      opt.style.backgroundColor = '#f2f2f2';
      opt.style.borderColor = 'transparent';
      opt.setAttribute('aria-pressed', 'false');
    });

    selectedOption.classList.add('is-selected');
    selectedOption.style.backgroundColor = '#fff';
    selectedOption.style.borderColor = '#d2242a';
    selectedOption.setAttribute('aria-pressed', 'true');
  }

  // Update preset visual state when quantity changes
  document.addEventListener('quantity:changed', function (e) {
    if (!e.detail) return;
    
    const quantity = e.detail.quantity;
    options.forEach(opt => {
      if (parseInt(opt.dataset.quantity) === quantity) {
        updateSelectedState(opt);
      }
    });
  });

  // Preset click handlers
  options.forEach(option => {
    option.addEventListener('click', function () {
      const quantity = parseInt(this.dataset.quantity);
      updateSelectedState(this);
      
      if (quantityInput) {
        quantityInput.value = quantity;
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  });

  container.updateSelectedState = updateSelectedState;
}

export default initPresetQuantity;
